import React, { useEffect, useMemo, useState, Suspense } from "react";
/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
// Removed useCC as it is not defined and the file is missing.
import CorediskMenu3dV2 from "./corediskmenu3d_v2";
import MobileCoreMenu from "./MobileCoreMenu";
import StudentPortal from "./StudentPortal";
import AdminQuickMenu from './AdminQuickMenu';
import Toast from './Toast';
import TronWalletModal from './TronWalletModal';
import WalletWelcome from './WalletWelcome';
import WalletSelector from './WalletSelector';

console.log('📱 Dashboard: Module loading...');

// Lazy-load heavy views so the orb is instant
const ControlCenter = React.lazy(() => import("./ControlCenter"));                    // admin-only
const StudentTavern = React.lazy(() => import("./StudentTavern"));
const Tavern = React.lazy(() => import("./Tavern"));                                  // Mobile tavern
const DesktopTavern = React.lazy(() => import("./DesktopTavern"));                    // Desktop tavern
const KioskMode = React.lazy(() => import("./KioskMode"));
const WaiverForm = React.lazy(() => import("./WaiverForm"));
const InstructorsChat = React.lazy(() => import("./InstructorsChat"));
const AttendanceAdmin = React.lazy(() => import("./AttendanceAdmin"));
const AdminMode = React.lazy(() => import("./AdminMode"));
const ShowMode = React.lazy(() => import("./ShowMode"));
const PromoRoulette = React.lazy(() => import("./PromoRoulette"));
const LeadsManager = React.lazy(() => import("./LeadsManager"));
const LeadsManagerDesktop = React.lazy(() => import("./LeadsManagerDesktop"));
const StoreOrder = React.lazy(() => import("./StoreOrder"));

// Student Portal is imported eagerly at top to avoid chunk-load failures when navigating

// Optional Settings placeholder (won't crash if missing)
const Settings = React.lazy(() => import("./Settings"));

// Admin wallets fallback (if context.isAdmin not provided)
const ADMIN_WALLETS = (process.env.REACT_APP_ADMIN_WALLETS || "")
  .split(",")
  .map(w => w.trim().toLowerCase())
  .filter(Boolean);

function useRole(wallet, isAdminFromContext) {
  const lw = (wallet || "").toLowerCase();
  const isAdminWallet = lw && ADMIN_WALLETS.includes(lw);
  const isAdmin = Boolean(isAdminFromContext || isAdminWallet);
  return { isAdmin };
}

// Error boundary so a single module never kills the dashboard
class Boundary extends React.Component {
  constructor(p) { super(p); this.state = { hasError: false, err: null }; }
  static getDerivedStateFromError(err) { return { hasError: true, err }; }
  componentDidCatch(err, info) { console.error("View crashed:", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 20 }}>
          <h3>Something went wrong in this view.</h3>
          <pre style={{ whiteSpace: "pre-wrap", opacity: 0.75 }}>{String(this.state.err || "")}</pre>
          <button className="btn" onClick={() => this.setState({ hasError: false, err: null })}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Dashboard() {
  // Handle disk menu selection
  function handleSelect(tab) {
    console.log('Dashboard: handleSelect called with:', tab);
    
    // Map MobileCoreMenu keys to proper view names
    const viewMapping = {
      'kiosk': 'kiosk',
      'admin': 'admin',
      'student': 'student-tavern',
      'instructor': 'instructor'
    };
    
    const mappedView = viewMapping[tab] || tab;
    console.log('Dashboard: Mapping view key to:', mappedView);

  // These views require only Admin PIN (no wallet required): ShowMode, PromoRoulette
  // NOTE: Leads and Waiver are intentionally available without PIN for easy student access.
  const pinOnly = ['showmode', 'promo-roulette'];
    if (pinOnly.includes(tab)) {
      setPendingView(mappedView);
      setShowPinModal(true);
      return;
    }

    setView(mappedView);
  }

  // Handle admin / waiver PIN submit
  function handlePinSubmit() {
    // If the pending view is the waiver, require the WAIVER_PIN; otherwise require the kiosk admin PIN
    if (pendingView === "waiver") {
      if (pinInput === WAIVER_PIN) {
        // grant access to waiver view only (do not mark as admin)
        setShowPinModal(false);
        setPinError("");
        setView("waiver");
        setPendingView(null);
      } else {
        setPinError("Incorrect Waiver PIN");
      }
      return;
    }

    // Default admin/kiosk PIN flow
    if (pinInput === KIOSK_ADMIN_PIN) {
      setIsAdmin(true);
      setShowPinModal(false);
      setPinError("");
      if (pendingView) {
        setView(pendingView);
        setPendingView(null);
      }
    } else {
      setPinError("Incorrect PIN");
    }
  }

  // TopBar component
  const TopBar = () => (
    <div className="dashboard-topbar" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 16px", background: "rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.1)"
    }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <button className="btn-outline" onClick={() => setView("home")}>⬅ Home</button>
        <div style={{ fontWeight: 700 }}>Outbox BJJ</div>
        <div style={{ opacity: 0.75, fontSize: 12 }}>Evolve • Empower • Excel</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div className="wallet-box" style={{
          background: "rgba(255,255,255,0.08)", padding: "6px 12px", borderRadius: 10, fontSize: 13
        }}>
          🔗 {headerWallet}
        </div>
        <AdminQuickMenu isAdmin={isAdmin} />
      </div>
      <Toast />
    </div>
  );

  // Render functions for router
  const renderControlCenter = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <ControlCenter 
            students={students}
            setStudents={setStudents}
            leads={leads}
            setLeads={setLeads}
            isAdmin={isAdmin}
            walletAddress={wallet}
            onBackToMenu={() => setView("home")}
          />
        </Boundary>
      </Suspense>
    </>
  );
  const renderAdminMode = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <AdminMode 
            students={students}
            setStudents={setStudents}
            attendanceByStudent={{}}
            weeklyPctFor={() => 0}
            monthlyPctFor={() => 0}
            account={wallet}
          />
        </Boundary>
      </Suspense>
    </>
  );
  const renderKiosk = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <KioskMode 
            students={students}
            setStudents={setStudents}
            isAdmin={isAdmin}
            walletAddress={wallet}
            onBackToMenu={() => setView("home")}
          />
        </Boundary>
      </Suspense>
    </>
  );
  const renderInstructor = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <InstructorsChat
            students={students}
            setStudents={setStudents}
            walletAddress={wallet}
            isAdmin={isAdmin}
            onBackToMenu={() => setView('home')}
            showToast={(m) => console.log('Toast:', m)}
          />
        </Boundary>
      </Suspense>
    </>
  );
  const renderAttendance = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}><Boundary><AttendanceAdmin /></Boundary></Suspense>
    </>
  );
  const renderStudentTavern = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <StudentTavern 
            walletAddress={wallet}
            isAdmin={isAdmin}
            onBackToMenu={() => setView("home")}
            onRequestWalletConnect={initiateWalletConnection}
            showToast={(msg) => console.log('Toast:', msg)}
            students={students}
            adminWallet={ADMIN_WALLET}
          />
        </Boundary>
      </Suspense>
    </>
  );

  // Authenticator state
  const ADMIN_WALLET = "0x8bfc802e8decc49b63158b9ec4122e04a22d819c";
  const [wallet, setWallet] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [pinError, setPinError] = useState("");
  const [pendingView, setPendingView] = useState(null);
  // PIN configuration
  const KIOSK_ADMIN_PIN = "310781"; // Admin PIN used by kiosk/admin flows
  const WAIVER_PIN = "555000"; // Separate PIN for Waiver-only access
  const [students, setStudents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState("home");
  const [currentStudentId, setCurrentStudentId] = useState(null);
  
  // Safe mobile detection with fallback for older browsers
  const [isMobile, setIsMobile] = useState(() => {
    console.log('📱 Dashboard: Initializing mobile detection...');
    try {
      if (typeof window === 'undefined') return false;
      // Check viewport width first (most reliable)
      if (window.innerWidth <= 768) {
        console.log('📱 Dashboard: Detected mobile via innerWidth:', window.innerWidth);
        return true;
      }
      // Try matchMedia if available
      if (window.matchMedia) {
        const result = window.matchMedia("(max-width: 768px)").matches;
        console.log('📱 Dashboard: Detected mobile via matchMedia:', result);
        return result;
      }
      // Fallback to user agent detection
      const uaResult = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      console.log('📱 Dashboard: Detected mobile via userAgent:', uaResult);
      return uaResult;
    } catch (e) {
      console.error('📱 Dashboard: Mobile detection failed:', e);
      return false;
    }
  });
  
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  const [walletType, setWalletType] = useState(null); // 'metamask' or 'phantom'
  
  useEffect(() => {
    console.log('📱 Dashboard: Setting up media query listener...');
    try {
      if (typeof window === 'undefined' || !window.matchMedia) {
        console.log('📱 Dashboard: matchMedia not available');
        return;
      }
      const mm = window.matchMedia("(max-width: 768px)");
      console.log('📱 Dashboard: Media query initial matches:', mm.matches);
      const onChange = e => {
        try {
          console.log('📱 Dashboard: Media query change detected:', e.matches);
          setIsMobile(e.matches);
        } catch (err) {
          console.warn('📱 Dashboard: matchMedia onChange error:', err);
        }
      };
      if (mm.addEventListener) {
        mm.addEventListener("change", onChange);
        return () => mm.removeEventListener("change", onChange);
      } else if (mm.addListener) {
        // Fallback for older browsers (Safari < 14)
        console.log('📱 Dashboard: Using legacy addListener');
        mm.addListener(onChange);
        return () => mm.removeListener(onChange);
      }
    } catch (e) {
      console.error('📱 Dashboard: matchMedia setup failed:', e);
    }
  }, []);

  // Optional: Auto-show wallet modal on page load (commented out to avoid blocking users)
  // Users can connect wallet via the menu or use PIN login instead
  // useEffect(() => {
  //   setShowWalletModal(true);
  //   setWallet(null);
  //   setIsAdmin(false);
  // }, []);

  // Load student data from server on mount
  useEffect(() => {
    const loadStudentData = async () => {
      try {
        console.log('Dashboard: Loading student data from server via loadDataFromServer()...');
        const { loadDataFromServer, apiFetch } = await import('../utils/serverConfig');
        let data = null;
        try {
          data = await loadDataFromServer();
        } catch (e) {
          console.warn('Dashboard: loadDataFromServer() failed:', e);
        }

        if (data && (Array.isArray(data) || data.students)) {
          const studentsPayload = Array.isArray(data) ? data : (data.students || []);
          setStudents(studentsPayload);
          console.log('Dashboard: Students state updated with', studentsPayload.length, 'students');
          if (data.products && Array.isArray(data.products)) setProducts(data.products);
          if (data.orders && Array.isArray(data.orders)) setOrders(data.orders);
          if (data.leads && Array.isArray(data.leads)) {
            setLeads(data.leads);
            try { localStorage.setItem('leads', JSON.stringify(data.leads)); } catch (e) {}
          }
        } else {
          // Fallback: attempt to fetch /api/data directly via apiFetch (will surface helpful hints)
          try {
            const res = await apiFetch('/api/data');
            const payload = await res.json();
            const studentsPayload = Array.isArray(payload) ? payload : (payload.students || []);
            if (studentsPayload && studentsPayload.length) {
              setStudents(studentsPayload);
              if (payload.products && Array.isArray(payload.products)) setProducts(payload.products);
              if (payload.orders && Array.isArray(payload.orders)) setOrders(payload.orders);
              if (payload.leads && Array.isArray(payload.leads)) {
                setLeads(payload.leads);
                try { localStorage.setItem('leads', JSON.stringify(payload.leads)); } catch (e) {}
              }
              console.log('Dashboard: Fetched /api/data via apiFetch. Students:', studentsPayload.length);
            } else {
              // Last-resort: localStorage fallback
              const savedStudents = localStorage.getItem('students');
              if (savedStudents) {
                const parsedStudents = JSON.parse(savedStudents);
                console.log('Dashboard: Using localStorage fallback:', parsedStudents.length, 'students');
                setStudents(parsedStudents);
              }
            }
          } catch (err) {
            console.warn('Dashboard: apiFetch /api/data attempt failed:', err);
            const savedStudents = localStorage.getItem('students');
            if (savedStudents) {
              const parsedStudents = JSON.parse(savedStudents);
              console.log('Dashboard: Using localStorage fallback after apiFetch failure:', parsedStudents.length, 'students');
              setStudents(parsedStudents);
            }
          }
        }
      } catch (error) {
        console.error('Dashboard: Error loading student data:', error);
        try {
          const savedStudents = localStorage.getItem('students');
          if (savedStudents) {
            const parsedStudents = JSON.parse(savedStudents);
            console.log('Dashboard: Using localStorage fallback after error:', parsedStudents.length, 'students');
            setStudents(parsedStudents);
          }
        } catch (fallbackError) {
          console.error('Dashboard: LocalStorage fallback also failed:', fallbackError);
        }
      }
    };

    loadStudentData();
    // Also fetch products and orders explicitly via centralized apiFetch
    (async () => {
      try {
        const { apiFetch } = await import('../utils/serverConfig');
        try {
          const pRes = await apiFetch('/api/products');
          const pj = await pRes.json();
          if (pj && Array.isArray(pj.products)) setProducts(pj.products);
        } catch (e) {
          // ignore product fetch failure
        }
        try {
          const oRes = await apiFetch('/api/orders');
          const oj = await oRes.json();
          if (oj && Array.isArray(oj.orders)) setOrders(oj.orders);
        } catch (e) {
          // ignore orders fetch failure
        }
      } catch (err) {
        // ignore
      }
    })();
  }, []); // Run once on mount

  // Listen for cross-window navigation events (e.g., WaiverForm -> Leads)
  useEffect(() => {
    const navHandler = (e) => {
      try {
        const detail = e?.detail || {};
        if (detail.view === 'leads' || detail.view === 'Leads' || detail.view === 'leads-manager') {
          setView('leads');
          setCurrentStudentId(null);
        }
        // Support explicit 'home' / 'main' navigation requests
        if (detail.view === 'home' || detail.view === 'main' || detail.view === 'dashboard') {
          setView('home');
          setCurrentStudentId(null);
        }
        if (detail.view === 'waiver') {
          setView('waiver');
          setCurrentStudentId(null);
        }
        if (detail.view === 'studentportal' || detail.view === 'studentPortal' || detail.view === 'student') {
          setView('studentportal');
          // accept currentStudentId, studentId or wallet mapping
          setCurrentStudentId(detail.currentStudentId || detail.studentId || null);
        }
      } catch (err) {
        console.warn('navigateTo handler error', err);
      }
    };
    window.addEventListener('navigateTo', navHandler);
    return () => window.removeEventListener('navigateTo', navHandler);
  }, []);

  // KPIs (read-only here)
  const totals = useMemo(() => {
    const totalRevenue = orders.reduce((s, o) => s + (Number(o.total) || 0), 0);
    return {
      revenue: totalRevenue,
      orders: orders.length,
      products: products.length,
      students: students.length,
    };
  }, [orders, products, students]);

  const headerWallet = wallet ? `${wallet.slice(0, 6)}…${wallet.slice(-4)}` : "Not connected";

  // Wallet connect — Phantom only
  const initiateWalletConnection = () => {
    connectPhantom();
  };

  const handleWalletSelection = async (selectedWallet) => {
    setShowWalletSelector(false);
    setWalletType(selectedWallet);
    if (selectedWallet === 'phantom') await connectPhantom();
  };

  const disconnectWallet = () => {
    setWallet(null);
    setIsAdmin(false);
    setWalletType(null);
    setShowWalletModal(false);
    setView('home');
  };

  // Phantom wallet connect (EVM address via Phantom's ethereum provider)
  const connectPhantom = async () => {
    try {
      // Prefer Phantom's dedicated EVM provider; fall back to window.ethereum only if Phantom injected it
      const provider = window.phantom?.ethereum
        ?? (window.ethereum?.isPhantom ? window.ethereum : null);

      if (!provider) {
        alert(
          '👻 Phantom Not Detected\n\n' +
          'Please install Phantom:\n' +
          '1. Visit phantom.app\n' +
          '2. Install the browser extension\n' +
          '3. Create or import a wallet\n' +
          '4. Refresh this page'
        );
        return;
      }

      setWallet(null);
      setIsAdmin(false);

      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      if (!accounts || accounts.length === 0) throw new Error('No accounts returned from Phantom');

      const addrLower = accounts[0].toLowerCase();
      const isAdminWallet = ADMIN_WALLETS.includes(addrLower);

      setWallet(addrLower);
      setWalletType('phantom');
      setIsAdmin(isAdminWallet);
      setShowWalletModal(true);

      // Store for use in minting / signing flows
      window._phantomProvider = provider;

    } catch (err) {
      console.error('❌ Phantom connection error:', err);
      if (err.code === 4001) {
        alert('🚫 Connection Cancelled\n\nYou declined the Phantom connection.\nClick Connect again when ready.');
      } else if (err.code === -32002) {
        alert('⏳ Connection Already Pending\n\nCheck the Phantom extension — there is a pending request waiting for approval.');
      } else {
        alert('⚠️ Phantom Connection Error\n\n' + err.message);
      }
      setWallet(null);
      setIsAdmin(false);
      setWalletType(null);
    }
  };

  // HOME (desktop orb or mobile radial)
  const renderHome = () => {
    try {
      return (
        <div className="dashboard" style={{
          minHeight: "100vh",
          background: "radial-gradient(circle at 30% 30%, #0f172a, #1e293b)",
          color: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: 24
        }}>
          <div style={{ position: "absolute", top: 16, left: 16, fontSize: 12, opacity: 0.85 }}>🔗 {headerWallet}</div>
          {isMobile ? (
            <Boundary>
              <MobileCoreMenu 
                onSelect={handleSelect} 
                walletAddress={wallet}
                isAdmin={isAdmin}
                onConnectWallet={initiateWalletConnection}
              />
            </Boundary>
          ) : (
            <Boundary>
              <CorediskMenu3dV2
                onSelect={handleSelect}
                wallet={wallet}
                isAdmin={isAdmin}
                onConnectWallet={initiateWalletConnection}
                onShowPinModal={() => setShowPinModal(true)}
              />
            </Boundary>
          )}
          
          {/* Wallet Selector Modal */}
          {showWalletSelector && (
            <WalletSelector
              onSelect={handleWalletSelection}
              onClose={() => setShowWalletSelector(false)}
            />
          )}
          
          {/* Wallet Welcome Modal */}
          {showWelcomeModal && (
            <WalletWelcome
              onConnect={connectPhantom}
              onCancel={() => setShowWelcomeModal(false)}
            />
          )}
          
          {showPinModal && (
        <div className="solana-modal-overlay" style={{ zIndex: 10000, backdropFilter: 'blur(8px) brightness(1.2)' }}>
          <div className="solana-modal" style={{ maxWidth: 440, boxShadow: '0 0 120px 40px #00fff7, 0 0 0 12px #fff2', border: '4px solid #00fff7', borderRadius: '32px', background: 'radial-gradient(circle at 60% 40%, rgba(0,255,247,0.18) 0%, rgba(34,34,34,0.95) 100%)', padding: '32px 36px', color: '#fff', position: 'relative' }}>
            <button className="solana-modal-close" style={{ position: 'absolute', top: 18, right: 18, fontSize: 24, color: '#00fff7', background: 'none', border: 'none', cursor: 'pointer', zIndex: 2 }} onClick={() => setShowPinModal(false)}>✕</button>
            <div className="solana-modal-header" style={{ textAlign: 'center', marginBottom: 18 }}>
              <div style={{ fontSize: 64, marginBottom: 12, color: '#00fff7', textShadow: '0 0 48px #00fff7' }}>🔐</div>
              <h2 className="solana-modal-title" style={{ fontSize: 32, fontWeight: 800, letterSpacing: 2, color: '#fff', textShadow: '0 0 24px #00fff7' }}>{pendingView === 'waiver' ? 'Waiver PIN Required' : 'Admin PIN Required'}</h2>
              <p className="solana-modal-desc" style={{ fontSize: 18, color: '#00fff7', marginTop: 8, marginBottom: 0, textShadow: '0 0 12px #00fff7' }}>{pendingView === 'waiver' ? 'Enter the Waiver PIN to access the waiver form.' : 'Enter your 6-digit PIN to unlock admin mode.'}</p>
              <div style={{ fontSize: 14, color: '#fff', marginTop: 8, opacity: 0.7 }}>Phantom wallet: <span style={{ color: '#00fff7', fontWeight: 700 }}>{wallet ? wallet : 'Not connected'}</span></div>
            </div>
            <div className="solana-modal-content" style={{ padding: '24px 0' }}>
              <input
                type="password"
                className="pin-input"
                value={pinInput}
                onChange={e => { setPinInput(e.target.value); setPinError(""); }}
                onKeyPress={e => { if (e.key === "Enter") handlePinSubmit(); }}
                placeholder="Enter PIN"
                maxLength={6}
                autoFocus
                style={{ width: '100%', padding: '18px', fontSize: '28px', textAlign: 'center', letterSpacing: '12px', border: '2px solid #00fff7', borderRadius: '16px', marginBottom: '18px', fontFamily: 'monospace', background: 'rgba(0,255,247,0.08)', color: '#fff', boxShadow: '0 0 24px #00fff7' }}
              />
              {pinError && (
                <div style={{ color: '#e74c3c', fontSize: '16px', marginBottom: '16px', textAlign: 'center', fontWeight: 600, textShadow: '0 0 8px #e74c3c' }}>{pinError}</div>
              )}
              <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
                <button style={{ flex: 1, padding: '16px', fontSize: '18px', fontWeight: 700, border: 'none', borderRadius: '12px', background: '#95a5a6', color: 'white', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 0 12px #95a5a6' }} onClick={() => { setShowPinModal(false); setPinInput(""); setPinError(""); }}>Cancel</button>
                <button style={{ flex: 1, padding: '16px', fontSize: '18px', fontWeight: 700, border: 'none', borderRadius: '12px', background: 'linear-gradient(135deg, #00fff7 0%, #6366f1 100%)', color: 'white', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 0 24px #00fff7' }} onClick={handlePinSubmit}>Unlock Admin</button>
              </div>
              <div style={{ marginTop: '24px', padding: '14px', background: 'rgba(0,255,247,0.08)', borderRadius: '10px', fontSize: '15px', color: '#fff', lineHeight: 1.5, textAlign: 'center', textShadow: '0 0 8px #00fff7' }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>ℹ️ Note:</div>
                This PIN unlocks PIN-only admin features (wallet NOT required). For full wallet-backed admin features, connect the admin Phantom wallet.
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Tron Wallet Modal - Shows after successful connection */}
      {showWalletModal && wallet && (
        <TronWalletModal 
          wallet={wallet}
          isAdmin={isAdmin}
          walletType={walletType}
          onClose={() => setShowWalletModal(false)}
          onEnterMenu={() => {
            setShowWalletModal(false);
            setView('home');
          }}
        />
      )}
        </div>
      );
    } catch (error) {
      console.error('renderHome error:', error);
      return (
        <div style={{
          minHeight: "100vh",
          background: "radial-gradient(circle at 30% 30%, #0f172a, #1e293b)",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          flexDirection: "column"
        }}>
          <h2>Loading Error</h2>
          <p>Failed to load dashboard. Please refresh the page.</p>
          <button className="btn" onClick={() => window.location.reload()}>Reload</button>
          <pre style={{ marginTop: 20, fontSize: 12, opacity: 0.7, maxWidth: '90vw', overflow: 'auto' }}>
            {error.message}
          </pre>
        </div>
      );
    }
  };

  const renderTavern = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          {isMobile ? (
            <Tavern onBackToMenu={() => setView('home')} />
          ) : (
            <DesktopTavern onBackToMenu={() => setView('home')} />
          )}
        </Boundary>
      </Suspense>
    </>
  );

  const renderStore = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <StoreOrder />
        </Boundary>
      </Suspense>
    </>
  );

  const renderFreeTrial = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}><Boundary><WaiverForm /></Boundary></Suspense>
    </>
  );

  const renderShowMode = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}><Boundary><ShowMode /></Boundary></Suspense>
    </>
  );

  const renderPromoRoulette = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}><Boundary><PromoRoulette /></Boundary></Suspense>
    </>
  );

  const renderLeadsManager = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          {isMobile ? (
            <LeadsManager />
          ) : (
            <LeadsManagerDesktop />
          )}
        </Boundary>
      </Suspense>
    </>
  );

  const renderStudentPortal = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}>
        <Boundary>
          <StudentPortal isAdmin={isAdmin} currentStudentId={currentStudentId} walletAddress={wallet} />
        </Boundary>
      </Suspense>
      
      {/* Wallet Welcome Modal */}
      {showWelcomeModal && (
        <WalletWelcome
          onConnect={connectPhantom}
          onCancel={() => setShowWelcomeModal(false)}
        />
      )}
      
      {/* Tron Wallet Modal */}
      {showWalletModal && wallet && (
        <TronWalletModal 
          wallet={wallet}
          isAdmin={isAdmin}
          onClose={() => setShowWalletModal(false)}
          onEnterMenu={() => {
            setShowWalletModal(false);
            setView('home');
          }}
        />
      )}
    </>
  );

  const renderSettings = () => (
    <>
      <TopBar />
      <Suspense fallback={<Loader />}><Boundary><Settings /></Boundary></Suspense>
    </>
  );

  // Router
  switch (view) {
    case "showmode":       return renderShowMode();
    case "promo-roulette": return renderPromoRoulette();
    case "leads":          return renderLeadsManager();
  case "studentportal": return renderStudentPortal();
    case "controlcenter":  return renderControlCenter();
    case "admin":          return renderAdminMode();
    case "kiosk":          return renderKiosk();
    case "instructor":     return renderInstructor();
    case "attendance":     return renderAttendance();
    case "student-tavern": return renderStudentTavern();
    case "tavern":         return renderTavern();
    // Accept both 'waiver' and the legacy 'freetrial' view keys
    case "waiver":
    case "freetrial":      return renderFreeTrial();
    case "settings":       return renderSettings();
    case "store":          return renderStore();
    default:                return renderHome();
  }
}

/* ---------- tiny helpers ---------- */
function Loader() {
  return (
    <div style={{ padding: 24, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "40vh", color: "#cbd5e1" }}>
      Loading…
    </div>
  );
}
const card = (borderColor) => ({
  background: "rgba(255,255,255,0.06)",
  borderRadius: 12,
  padding: 16,
  textAlign: "center",
  borderLeft: `4px solid ${borderColor}`,
  backdropFilter: "blur(8px)"
});
const kpiVal = { fontSize: 20, fontWeight: 700 };
const kpiLbl = { fontSize: 12, opacity: 0.8 };
